import { useEffect, useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Star, MousePointerClick } from "lucide-react";

const lineVar = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: 0,
    transition: { delay: 0.2 + i * 0.16, duration: 0.9, ease: [0.16, 1, 0.3, 1] },
  }),
};

const Starfield = () => {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let w, h, stars, raf;
    const resize = () => {
      w = canvas.width = canvas.offsetWidth;
      h = canvas.height = canvas.offsetHeight;
      const colors = ["#0B132B", "#1D4ED8", "#EE1C24", "#1D4ED8", "#FFB703"];
      stars = Array.from({ length: 130 }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        r: Math.random() * 1.6 + 0.3,
        s: Math.random() * 0.25 + 0.05,
        c: colors[Math.floor(Math.random() * colors.length)],
      }));
    };
    resize();
    window.addEventListener("resize", resize);
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      for (const st of stars) {
        st.y -= st.s;
        if (st.y < -2) st.y = h + 2;
        ctx.globalAlpha = 0.75;
        ctx.fillStyle = st.c;
        ctx.beginPath();
        ctx.arc(st.x, st.y, st.r, 0, Math.PI * 2);
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);
  return <canvas ref={ref} className="absolute inset-0 w-full h-full" aria-hidden="true" />;
};

const ProductSpotlight = ({ parallaxX, parallaxY }) => (
  <motion.div
    style={{ x: parallaxX, y: parallaxY }}
    className="relative"
    data-testid="hero-product-visual"
  >
    <div
      className="absolute -top-16 left-1/2 -translate-x-1/2 w-72 h-96 opacity-50 pointer-events-none"
      style={{
        background: "linear-gradient(to bottom, rgba(238,28,36,0.16), transparent 75%)",
        clipPath: "polygon(42% 0, 58% 0, 100% 100%, 0 100%)",
      }}
    />
    <motion.div
      animate={{ y: [0, -12, 0] }}
      transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
      className="relative"
    >
      <img
        src="/images/master-blaster-hero.png"
        alt="The Master Blaster one-footed poop stool in action"
        data-testid="hero-product-image"
        className="w-full max-w-xl rounded-lg border-4 border-cream shadow-[0_30px_60px_rgba(238,28,36,0.22)] -rotate-1"
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.2, duration: 0.5 }}
        className="absolute -top-4 -right-4 sm:-right-8 bg-gold text-[#0B132B] font-jbmono text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-sm rotate-6"
      >
        One-Foot Certified
      </motion.div>
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.5, duration: 0.5 }}
        className="absolute -bottom-3 -left-4 sm:-left-8 bg-white border border-steel text-cream font-jbmono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-sm -rotate-3 shadow-md"
      >
        Sphincter Approved
      </motion.div>
    </motion.div>
  </motion.div>
);

export const Hero = ({ onPreorder }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 50, damping: 20 });
  const sy = useSpring(my, { stiffness: 50, damping: 20 });
  const parallaxX = useTransform(sx, [-0.5, 0.5], [-18, 18]);
  const parallaxY = useTransform(sy, [-0.5, 0.5], [-12, 12]);

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      onMouseMove={onMouseMove}
      className="relative grain-overlay overflow-hidden"
    >
      <Starfield />
      <div className="absolute inset-0 bg-gradient-to-b from-navy/40 via-transparent to-navy pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-8 pt-16 pb-24 sm:pt-24 sm:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="font-jbmono text-xs uppercase tracking-[0.25em] text-ice mb-6 flex items-center gap-2"
            data-testid="hero-eyebrow"
          >
            <Star className="w-3.5 h-3.5 fill-ice" />
            America's first one-footed poop stool
          </motion.p>
          <h1 className="font-display font-black uppercase leading-[0.9] tracking-tight text-6xl sm:text-8xl lg:text-9xl">
            <span className="block overflow-hidden">
              <motion.span custom={0} variants={lineVar} initial="hidden" animate="show" className="block text-crimson">
                Master
              </motion.span>
            </span>
            <span className="block overflow-hidden">
              <motion.span custom={1} variants={lineVar} initial="hidden" animate="show" className="block text-[#1D4ED8]">
                Blaster
              </motion.span>
            </span>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-6 font-display font-bold uppercase text-xl sm:text-2xl tracking-wide text-crimson"
            data-testid="hero-tagline"
          >
            Unleash the sphincter. Elevate one foot.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7 }}
            className="mt-4 max-w-xl text-base sm:text-lg leading-relaxed text-cream/70"
          >
            Not two feet. <span className="text-cream font-semibold">One.</span> The asymmetric lift that
            relaxes your sphincter muscles and lets it flow. Pre-order today — officially on sale Christmas Day.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.7 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={onPreorder}
              data-testid="hero-preorder-button"
              className="bg-crimson hover:bg-crimsondark text-white font-display font-black uppercase tracking-wide text-lg px-8 py-4 rounded-sm transition-colors duration-200 shadow-[0_10px_40px_rgba(238,28,36,0.4)]"
            >
              Pre-Order Your Master Blaster Today
            </button>
            <a
              href="#how-it-works"
              data-testid="hero-how-it-works-button"
              className="flex items-center gap-2 border border-steel hover:border-gold text-cream/80 hover:text-gold font-jbmono text-xs uppercase tracking-[0.2em] px-6 py-4 rounded-sm transition-colors duration-200"
            >
              <MousePointerClick className="w-4 h-4" />
              See how it works
            </a>
          </motion.div>
        </div>
        <div className="lg:col-span-5">
          <ProductSpotlight parallaxX={parallaxX} parallaxY={parallaxY} />
        </div>
      </div>
    </section>
  );
};
